import './Map.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import { default as ReactMap, Layer, Source } from 'react-map-gl';

// eslint-disable-next-line import/no-webpack-loader-syntax
import maplibregl from '!maplibre-gl';      // ! is important here
import maplibreglWorker from 'maplibre-gl/dist/maplibre-gl-csp-worker';
import FeatureService from 'mapbox-gl-arcgis-featureserver'

import MapStyleSwitch, { mapStyles } from './MapStyleSwitch';
import { useSelector } from 'react-redux';
import { layers } from '../config/map.constants';

maplibregl.workerClass = maplibreglWorker;

const navigationControl = new maplibregl.NavigationControl();

const Map = ({onMapLoad, onSourcesLoaded, children, mapLayers, sources, center, bounds, navControls, ...mapOpts}) => {
	// const mapRef = useRef();

	const isOnline = useSelector(state => state.cache.isOnline);
	const [map, setMap] = useState();
	const [layersSources, setLayersSources] = useState([]);
	const [layerService, setLayerService] = useState({}); // {[layerId: string]: FeatureService]}
	const [mapStyle, setMapStyle] = useState(mapStyles[isOnline ? 1 : 2].uri);

	const [layerImagesLoaded, setLayerImagesLoaded] = useState(false);
	const [sourceImagesLoaded, setSourceImagesLoaded] = useState(false);

	useEffect(() => {
		if (!map || !sources) return;
		Promise.all(sources.map(source => {
			if (!source.markerImage) return;
			return loadMarkerImage(map, source);
		})).then( setSourceImagesLoaded(true));
	}, [map, sources])

	useEffect(() => {
		if (!map || !mapLayers) return;
		Promise.all(mapLayers.map(layer => {
			if (!layer.markerImage) return;
			return loadMarkerImage(map, layer);
		})).then( setLayerImagesLoaded(true));
	}, [map, mapLayers])

	useEffect(() => {
		if (!map) return;
		if(navControls && !map.hasControl(navigationControl)) map.addControl(navigationControl);
		else if (!navControls && map.hasControl(navigationControl)) map.removeControl(navigationControl);
		onMapLoad(map);
	}, [map, navControls])

	useEffect(() => {
		map && bounds && map.fitBounds(bounds);
	}, [bounds, map])

	useEffect(() => {
		if (!map) return;
		const bounds = map.getBounds();
		const services = mapLayers.map(layer => {
			if (!layer.url) return null;
			return initFeatureLayer({
				...layer,
				arcGisOpts: {
					...layer.arcGisOpts,
					geometry: `{xmin: ${bounds.getNorthWest().lng}, ymin: ${bounds.getNorthWest().lat}, xmax: ${bounds.getSouthEast().lng}, ymax: ${bounds.getSouthEast().lat}}`,
					// where: `1=1`
					// where: `${layer.objectIdField} IN ('${objectIds.join("','")}')`
				}
			});
		}).filter(v => !!v);

		let lServices = {};
		services.forEach(({id, service}) => lServices[id] = service);
		setLayerService(lServices);
	}, [mapLayers, map])

	useEffect(() => {
		setLayersSources(mapLayers);
	}, [layerService])

	const loadMarkerImage = useCallback((map, layer) => {
		if (map.hasImage(layer.layerId || layer.id)) return;
		return new Promise((resolve) => {
			map.loadImage(layer.markerImage, (err, im) => {
				try {
					map.addImage(layer.layerId || layer.id, im);
				} catch(e){
					resolve();
				}
				resolve(im);
			})
		});
	}, [])
	
	const onMapLoaded = (e) => {
		const map = e.target
		setMap(map);
	}

	const loadedSourcesRef = useRef(new Set());
	const onSourceData = e => {
		if (!map) return;
		if (e.isSourceLoaded) loadedSourcesRef.current.add(e.sourceId);
	}

	const onIdle = (e) => {
		onSourcesLoaded && onSourcesLoaded(loadedSourcesRef.current.values());
	}

	const initFeatureLayer = layer => {
		if (!layer || !map) return;
		if (map.getSource(layer.source)) {
			return {id: layer.id, service: {sourceId: layer.source}}
		}
		const {url, source, arcGisOpts } = layer;
		try {
			const lService = new FeatureService(source, map, { url, ...arcGisOpts });
			return {id: layer.id, service: lService}
		} catch(e) {
			return ;
		}
	}

	// Ugly hack to fix layers disappearing on basemap change
	const [hide, setHide] = useState(false);
	const [baseMap, setBaseMap] = useState(1);
	const onStyleSwitched = (newBaseMap) => {
		setBaseMap(newBaseMap);
		setMapStyle(mapStyles[newBaseMap].uri);
		setHide(true);
		setTimeout(() => {
			setHide(false)
		}, 1)
	}

	return (!hide && 
		<ReactMap
			// ref={mapRef}
			mapLib={maplibregl}
			onLoad={onMapLoaded}
			onSourceData={onSourceData}
			onIdle={onIdle}
			initialViewState={center || null}
			bounds={bounds || null}
			mapStyle={mapStyle}
			styleDiffing={true}
			reuseMaps={false}
			{...mapOpts}>

			{/* Widgets */}
			{!!isOnline && <MapStyleSwitch key="style-switch" map={map} onSwitch={onStyleSwitched} initStyle={baseMap}/>}

			{/* Sources: Layers, etc */}
			{(sourceImagesLoaded && !!sources && !!sources.length ) &&
				sources.map((s) => (
					<Source key={s.layerId} id={s.layerId} type="geojson" data={s}>
						<Layer id={s.layerId} icon-anchor="center" {...layers[s.layerId]} />
					</Source>
				))
			}

			{(layerImagesLoaded && layersSources && !!layersSources.length ) &&
				layersSources.map((l) => (
					layerService[l.id] ? <Layer key={l.id} {...l} icon-anchor="center" source={layerService[l.id].sourceId} /> : null
				))
			}

			{children}

		</ReactMap>
	)
}

export default Map;